import { z } from "zod";
import { protocolIdSchema, verificationStatusSchema, } from "./identifiers.js";
import { foldVerificationStatuses } from "../verdict-law.js";
export const completionObligationClassificationSchema = z.enum([
    "task_outcome",
    "baseline",
    "safety",
    "effect_closure",
]);
export const completionOutcomeSchema = z
    .object({
    id: protocolIdSchema,
    statement: z.string().trim().min(1),
    sourceSpan: z.string().trim().min(1),
})
    .strict();
function compareProtocolIds(left, right) {
    return left < right ? -1 : left > right ? 1 : 0;
}
export function sortProtocolIds(values) {
    return [...values].sort(compareProtocolIds);
}
function addDuplicateIssues(values, context, path, label) {
    if (new Set(values).size !== values.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path,
            message: `${label} must not contain duplicate canonical IDs`,
        });
    }
}
export const canonicalProtocolIdSetSchema = z
    .array(protocolIdSchema)
    .superRefine((values, context) => {
    addDuplicateIssues(values, context, [], "canonical ID set");
})
    .transform(sortProtocolIds);
export const nonemptyCanonicalProtocolIdSetSchema = z
    .array(protocolIdSchema)
    .min(1)
    .superRefine((values, context) => {
    addDuplicateIssues(values, context, [], "canonical ID set");
})
    .transform(sortProtocolIds);
const taskOutcomesCompletionIntentObjectSchema = z
    .object({
    kind: z.literal("task_outcomes"),
    outcomes: z.array(completionOutcomeSchema).min(1),
})
    .strict();
const maintenanceBaselineCompletionIntentObjectSchema = z
    .object({
    kind: z.literal("maintenance_baseline"),
    rationale: z.string().trim().min(1),
})
    .strict();
export const completionIntentSchema = z
    .discriminatedUnion("kind", [
    taskOutcomesCompletionIntentObjectSchema,
    maintenanceBaselineCompletionIntentObjectSchema,
])
    .superRefine((value, context) => {
    if (value.kind === "task_outcomes") {
        addDuplicateIssues(value.outcomes.map((outcome) => outcome.id), context, ["outcomes"], "completion outcome IDs");
    }
})
    .transform((value) => value.kind === "task_outcomes"
    ? {
        ...value,
        outcomes: [...value.outcomes].sort((left, right) => compareProtocolIds(left.id, right.id)),
    }
    : value);
export const completionDefinitionReasonCodeSchema = z.enum([
    "completion-intent-invalid",
    "completion-definition-empty",
    "completion-reference-empty",
    "completion-reference-duplicate",
    "completion-source-unknown",
    "completion-outcome-unknown",
    "completion-coverage-invalid",
    "completion-baseline-provenance-invalid",
    "task-outcome-required",
    "work-outcome-uncovered",
    "maintenance-baseline-invalid",
]);
const REASON_ORDER = new Map(completionDefinitionReasonCodeSchema.options.map((code, index) => [code, index]));
function sortedIssueReferences(values) {
    return sortProtocolIds([...new Set(values)]);
}
function comparePaths(left, right) {
    return JSON.stringify(left).localeCompare(JSON.stringify(right));
}
function orderIssues(issues) {
    return issues.sort((left, right) => {
        const byReason = REASON_ORDER.get(left.code) - REASON_ORDER.get(right.code);
        if (byReason !== 0)
            return byReason;
        const byPath = comparePaths(left.path, right.path);
        if (byPath !== 0)
            return byPath;
        return left.referenceIds.join("\0").localeCompare(right.referenceIds.join("\0"));
    });
}
function canonicalIds(value) {
    if (!Array.isArray(value))
        return [];
    return value.flatMap((candidate) => {
        const parsed = protocolIdSchema.safeParse(candidate);
        return parsed.success ? [parsed.data] : [];
    });
}
function pushReferenceShapeIssues(input) {
    if (!input.emptyAllowed && input.values.length === 0) {
        input.issues.push({
            code: "completion-reference-empty",
            path: input.path,
            message: `${input.label} requires at least one canonical ID`,
            referenceIds: [],
        });
    }
    const duplicates = input.values.filter((value, index) => input.values.indexOf(value) !== index);
    if (duplicates.length > 0) {
        input.issues.push({
            code: "completion-reference-duplicate",
            path: input.path,
            message: `${input.label} contains duplicate canonical IDs`,
            referenceIds: sortedIssueReferences(duplicates),
        });
    }
}
export function completionDefinitionIssues(input) {
    const issues = [];
    const intentResult = completionIntentSchema.safeParse(input.completionIntent);
    if (!intentResult.success) {
        return [
            {
                code: "completion-intent-invalid",
                path: ["completionIntent"],
                message: "work and contract content require an explicit canonical completion intent",
                referenceIds: [],
            },
        ];
    }
    const intent = intentResult.data;
    const sources = Array.isArray(input.sources) ? input.sources : [];
    const sourceKinds = new Map();
    for (const source of sources) {
        const id = protocolIdSchema.safeParse(source.id);
        if (id.success && typeof source.kind === "string") {
            sourceKinds.set(id.data, source.kind);
        }
    }
    const outcomeIds = intent.kind === "task_outcomes"
        ? new Set(intent.outcomes.map((outcome) => outcome.id))
        : new Set();
    const obligations = Array.isArray(input.obligations) ? input.obligations : [];
    const required = obligations.filter((obligation) => obligation.required === true);
    if (required.length === 0) {
        issues.push({
            code: "completion-definition-empty",
            path: ["obligations"],
            message: "a completion definition requires at least one required obligation",
            referenceIds: [],
        });
    }
    const requiredTaskCoverage = new Set();
    let requiredTaskOutcomeCount = 0;
    let validRequiredBaselineCount = 0;
    obligations.forEach((obligation, index) => {
        const obligationId = protocolIdSchema.safeParse(obligation.id);
        const reference = obligationId.success ? obligationId.data : `obligation[${index}]`;
        const classification = completionObligationClassificationSchema.safeParse(obligation.classification);
        const sourceIds = canonicalIds(obligation.sourceIds);
        const coversOutcomeIds = canonicalIds(obligation.coversOutcomeIds);
        pushReferenceShapeIssues({
            issues,
            values: sourceIds,
            path: ["obligations", index, "sourceIds"],
            label: `obligation ${reference} sourceIds`,
            emptyAllowed: false,
        });
        const unknownSources = sourceIds.filter((sourceId) => !sourceKinds.has(sourceId));
        if (unknownSources.length > 0) {
            issues.push({
                code: "completion-source-unknown",
                path: ["obligations", index, "sourceIds"],
                message: `obligation ${reference} cites unknown contract sources`,
                referenceIds: sortedIssueReferences(unknownSources),
            });
        }
        if (!classification.success) {
            issues.push({
                code: "completion-coverage-invalid",
                path: ["obligations", index, "classification"],
                message: `obligation ${reference} requires a completion classification`,
                referenceIds: [],
            });
            return;
        }
        const isTaskOutcome = classification.data === "task_outcome";
        pushReferenceShapeIssues({
            issues,
            values: coversOutcomeIds,
            path: ["obligations", index, "coversOutcomeIds"],
            label: `obligation ${reference} coversOutcomeIds`,
            emptyAllowed: !isTaskOutcome,
        });
        if ((!isTaskOutcome && coversOutcomeIds.length > 0) ||
            (isTaskOutcome && coversOutcomeIds.length === 0)) {
            issues.push({
                code: "completion-coverage-invalid",
                path: ["obligations", index, "coversOutcomeIds"],
                message: isTaskOutcome
                    ? `task-outcome obligation ${reference} must cover at least one requested outcome`
                    : `${classification.data} obligation ${reference} cannot cover task outcomes`,
                referenceIds: coversOutcomeIds,
            });
        }
        const unknownOutcomes = coversOutcomeIds.filter((outcomeId) => !outcomeIds.has(outcomeId));
        if (unknownOutcomes.length > 0) {
            issues.push({
                code: "completion-outcome-unknown",
                path: ["obligations", index, "coversOutcomeIds"],
                message: `obligation ${reference} cites unknown requested outcomes`,
                referenceIds: sortedIssueReferences(unknownOutcomes),
            });
        }
        if (classification.data === "baseline") {
            const policyBacked = sourceIds.some((sourceId) => {
                const kind = sourceKinds.get(sourceId);
                return kind === "organization_policy" || kind === "repository_policy";
            });
            if (!policyBacked) {
                issues.push({
                    code: "completion-baseline-provenance-invalid",
                    path: ["obligations", index, "sourceIds"],
                    message: `baseline obligation ${reference} requires frozen organization or repository policy provenance`,
                    referenceIds: sourceIds,
                });
            }
            else if (obligation.required === true) {
                validRequiredBaselineCount += 1;
            }
        }
        if (isTaskOutcome && obligation.required === true) {
            requiredTaskOutcomeCount += 1;
            for (const outcomeId of coversOutcomeIds) {
                if (outcomeIds.has(outcomeId))
                    requiredTaskCoverage.add(outcomeId);
            }
        }
    });
    if (intent.kind === "task_outcomes") {
        if (requiredTaskOutcomeCount === 0) {
            issues.push({
                code: "task-outcome-required",
                path: ["obligations"],
                message: "ordinary work requires at least one required task-outcome obligation",
                referenceIds: intent.outcomes.map((outcome) => outcome.id),
            });
        }
        const uncovered = intent.outcomes
            .map((outcome) => outcome.id)
            .filter((outcomeId) => !requiredTaskCoverage.has(outcomeId));
        if (uncovered.length > 0) {
            issues.push({
                code: "work-outcome-uncovered",
                path: ["completionIntent", "outcomes"],
                message: "every requested outcome requires coverage from a required task-outcome obligation",
                referenceIds: sortProtocolIds(uncovered),
            });
        }
    }
    else if (validRequiredBaselineCount === 0) {
        issues.push({
            code: "maintenance-baseline-invalid",
            path: ["obligations"],
            message: "maintenance work requires a required baseline obligation backed by frozen policy",
            referenceIds: [],
        });
    }
    return orderIssues(issues);
}
export class CompletionDefinitionRefusal extends Error {
    code;
    reasonCodes;
    issues;
    constructor(issues) {
        const ordered = orderIssues([...issues]);
        super(ordered
            .map((issue) => `${issue.code}: ${issue.message}${issue.referenceIds.length > 0 ? ` [${issue.referenceIds.join(", ")}]` : ""}`)
            .join("; "));
        this.name = "CompletionDefinitionRefusal";
        this.code = ordered[0]?.code ?? "completion-intent-invalid";
        this.reasonCodes = [...new Set(ordered.map((issue) => issue.code))];
        this.issues = ordered;
    }
}
export function parseRequiredCompletionIntent(input) {
    const parsed = completionIntentSchema.safeParse(input);
    if (parsed.success)
        return parsed.data;
    throw new CompletionDefinitionRefusal([
        {
            code: "completion-intent-invalid",
            path: ["completionIntent"],
            message: "work and contract content require an explicit canonical completion intent",
            referenceIds: [],
        },
    ]);
}
export function assertCompletionDefinition(input) {
    const issues = completionDefinitionIssues(input);
    if (issues.length > 0)
        throw new CompletionDefinitionRefusal(issues);
}
export function addCompletionDefinitionZodIssues(input, context) {
    for (const issue of completionDefinitionIssues(input)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: issue.path,
            message: `${issue.code}: ${issue.message}`,
            params: { reasonCode: issue.code, referenceIds: issue.referenceIds },
        });
    }
}
export const completionClassSummarySchema = z
    .object({
    classification: completionObligationClassificationSchema,
    status: verificationStatusSchema,
    obligationIds: nonemptyCanonicalProtocolIdSetSchema,
})
    .strict();
export const completionClassSummariesSchema = z
    .object({
    taskOutcome: completionClassSummarySchema.nullable(),
    baselineHealth: completionClassSummarySchema.nullable(),
    safety: completionClassSummarySchema.nullable(),
    effectClosure: completionClassSummarySchema.nullable(),
})
    .strict();
export const COMPLETION_CLASS_SUMMARY_KEYS = {
    task_outcome: "taskOutcome",
    baseline: "baselineHealth",
    safety: "safety",
    effect_closure: "effectClosure",
};
/**
 * The fold over bare statuses. The precedence order used to be written out a
 * second time here, beside the copy in `verdict.ts`; both now read the one law.
 * Statuses alone carry no evaluator authority, so the PASS authority floor is
 * not evaluable at this entry point — see `UNATTRIBUTED_AUTHORITY`.
 */
export function worstVerificationStatus(statuses) {
    return foldVerificationStatuses(statuses);
}
